import { IQuarterlyMarketTableRow } from 'partner360/models/report/sobAnalyticsReports/iQuarterlyMarketTableRow'
import { IQuarterlyPartnerTableRow } from 'partner360/models/report/sobAnalyticsReports/iQuarterlyPartnerTableRow'

export const getFieldWithSellType = <T extends object>(
  baseField: string,
  sellType: string
): keyof T => (sellType === 'Ndp' ? baseField : `${baseField}${sellType}`) as keyof T

type TCurrYearBaseField =
  | 'revenue'
  | 'sob'
  | 'yoyPP'
  | 'qoq'
  | 'vsPlan'
  | 'salesPerformance'
  | 'yoyGrowth'
  | 'tam'
  | 'tamYoY'

const ACTUALS_BASE_FIELDS: Record<TCurrYearBaseField, keyof IQuarterlyMarketTableRow> = {
  revenue: 'currYearActualsRevenue',
  sob: 'currYearSobActual',
  yoyPP: 'prevYearYoYPP',
  qoq: 'currYearQoQ',
  vsPlan: 'prevYearVsPlan',
  salesPerformance: 'currYearSalesPerformance',
  yoyGrowth: 'currYearYoYGrowth',
  tam: 'currYearQuarterTamActuals',
  tamYoY: 'partnerTamActualsYoY',
}

const EXPECTED_EXIT_BASE_FIELDS: Record<TCurrYearBaseField, keyof IQuarterlyMarketTableRow> = {
  revenue: 'currYearExpExitRevenue',
  sob: 'currYearSobExpExit',
  yoyPP: 'expExitYoYPP',
  qoq: 'expExitQoQ',
  vsPlan: 'expExitVsPlan',
  salesPerformance: 'expExitSalesPerformance',
  yoyGrowth: 'expExitYoYGrowth',
  tam: 'currYearQuarterTamExpExit',
  tamYoY: 'partnerTamExpExitYoY',
}

// base fields (NDP names) of the current-year columns the Actuals / Expected Exit switch swaps
export const getCurrYearBaseFields = (isExpectedExit = false) =>
  isExpectedExit ? EXPECTED_EXIT_BASE_FIELDS : ACTUALS_BASE_FIELDS

export const getCurrentYearRevenueActuals = (
  sellType: string,
  item: IQuarterlyMarketTableRow | IQuarterlyPartnerTableRow,
  isExpectedExit = false
) => {
  const currentYearRevenueActualsField = getFieldWithSellType(
    getCurrYearBaseFields(isExpectedExit).revenue,
    sellType
  )

  const currentYearRevenueActualsValue = item[currentYearRevenueActualsField]
  return { currentYearRevenueActualsField, currentYearRevenueActualsValue }
}

export const getPrevYearRevenueActuals = (
  sellType: string,
  item: IQuarterlyMarketTableRow | IQuarterlyPartnerTableRow
) => {
  const prevYearRevenueActualsField = getFieldWithSellType('currYearActualsRevenue', sellType)

  const prevYearRevenueActualsValue = item[prevYearRevenueActualsField]
  return { prevYearRevenueActualsField, prevYearRevenueActualsValue }
}

export const getCurYearSobActuals = (
  sellType: string,
  item: IQuarterlyMarketTableRow | IQuarterlyPartnerTableRow,
  isExpectedExit = false
) => {
  const sobField = getFieldWithSellType(getCurrYearBaseFields(isExpectedExit).sob, sellType)

  return { curYearSobActualsField: sobField, curYearSobActualsValue: item[sobField] }
}

export const getPrevYearSobActuals = (
  sellType: string,
  item: IQuarterlyMarketTableRow | IQuarterlyPartnerTableRow
) => {
  const sobField = getFieldWithSellType('prevYearSobActual', sellType)

  return { prevYearSobActualsField: sobField, prevYearSobActualsValue: item[sobField] }
}
